import { HelpCircle, Lock } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Button } from '@/components/occurrences/button'
import { Card } from '@/components/ui/card'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { CLASSIFICATIONS, POTENTIAL_INFO } from '@/mocks/occurrences/catalog'
import type {
	ClassificationCode,
	Confidentiality,
	OccurrenceStatus,
	Potential,
} from '@/types/occurrence'
import { CONFIDENTIALITY_LABEL, STATUS_LABEL } from '@/types/occurrence'

const STATUS_STYLE: Record<OccurrenceStatus, string> = {
	DRAFT: 'bg-kaefer-light-silver text-kaefer-dark-grey',
	REGISTERED: 'bg-kaefer-astral-blue/15 text-kaefer-sapphire-blue',
	AWAITING_CLASSIFICATION: 'bg-kaefer-honey-yellow/20 text-kaefer-dark-grey',
	INVESTIGATING: 'bg-kaefer-pumpkin-orange/20 text-kaefer-dark-grey',
	AWAITING_VALIDATION: 'bg-kaefer-violet/15 text-kaefer-violet',
	ACTION_PLAN: 'bg-kaefer-sapphire-blue/15 text-kaefer-ocean-blue',
	AWAITING_EFFICACY: 'bg-kaefer-fuchsia-pink/15 text-kaefer-fuchsia-pink',
	CLOSED: 'bg-kaefer-grass-green/20 text-kaefer-forest-green',
	CANCELLED: 'bg-kaefer-red/15 text-kaefer-red',
	REOPENED: 'bg-kaefer-raspberry-pink/15 text-kaefer-raspberry-pink',
}

export const StatusBadge = ({
	status,
	className,
}: {
	status: OccurrenceStatus
	className?: string
}) => (
	<span
		className={cn(
			'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
			STATUS_STYLE[status],
			className,
		)}
	>
		{STATUS_LABEL[status]}
	</span>
)

const POTENTIAL_STYLE: Record<Potential, string> = {
	N1: 'border-rose-600 bg-rose-50 text-rose-700',
	N2: 'border-orange-500 bg-orange-50 text-orange-700',
	N3: 'border-yellow-500 bg-yellow-50 text-yellow-700',
	N4: 'border-teal-600 bg-teal-50 text-teal-700',
	N5: 'border-teal-500 bg-teal-100 text-teal-800',
}

export const PotentialPill = ({
	value,
	className,
}: {
	value: Potential
	className?: string
}) => (
	<span
		className={cn(
			'inline-flex items-center rounded-full border-2 px-2 py-0.5 text-xs font-bold',
			POTENTIAL_STYLE[value],
			className,
		)}
		title={POTENTIAL_INFO[value]}
	>
		{value}
	</span>
)

export const ClassificationChip = ({
	code,
}: {
	code: ClassificationCode | null
}) => (
	<span className="inline-flex rounded-md bg-kaefer-dark-grey px-2 py-0.5 text-xs font-semibold text-white">
		{code ? CLASSIFICATIONS[code].label.split(' — ')[0] : '—'}
	</span>
)

export const ConfidentialityBadge = ({ value }: { value: Confidentiality }) => (
	<span
		className={cn(
			'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
			value === 'RESTRICTED' && 'bg-kaefer-red/15 text-kaefer-red',
			value === 'INTERNAL' && 'bg-kaefer-honey-yellow/20 text-kaefer-dark-grey',
			value === 'PUBLIC' && 'bg-kaefer-light-silver text-kaefer-dark-grey',
		)}
	>
		{value === 'RESTRICTED' && <Lock className="size-3" />}
		{CONFIDENTIALITY_LABEL[value]}
	</span>
)

export const ProgressBar = ({
	value,
	className,
}: {
	value: number
	className?: string
}) => (
	<div className={cn('flex items-center gap-2', className)}>
		<div className="h-2 flex-1 overflow-hidden rounded-full bg-kaefer-light-silver">
			<div
				className={cn(
					'h-full rounded-full transition-all',
					value === 100 ? 'bg-kaefer-grass-green' : 'bg-kaefer-honey-yellow',
				)}
				style={{ width: `${value}%` }}
			/>
		</div>
		<span className="w-9 text-right text-xs font-semibold tabular-nums text-kaefer-dark-grey">
			{value}%
		</span>
	</div>
)

/** Bloco de seção padrão dentro das páginas da ocorrência. */
export const Panel = ({
	title,
	description,
	actions,
	children,
	className,
}: {
	title?: string
	description?: string
	actions?: ReactNode
	children: ReactNode
	className?: string
}) => (
	<Card className={cn('gap-0 py-0', className)}>
		{(title || actions) && (
			<div className="flex flex-wrap items-start justify-between gap-2 border-b px-4 py-3 sm:px-5">
				<div>
					{title && (
						<h2 className="text-base font-semibold text-kaefer-dark-grey">
							{title}
						</h2>
					)}
					{description && (
						<p className="mt-0.5 text-xs text-muted-foreground">
							{description}
						</p>
					)}
				</div>
				{actions}
			</div>
		)}
		<div className="space-y-4 p-4 sm:p-5">{children}</div>
	</Card>
)

export const Field = ({
	label,
	required,
	hint,
	children,
	className,
}: {
	label: string
	required?: boolean
	hint?: string
	children: ReactNode
	className?: string
}) => (
	<div className={cn('space-y-1.5', className)}>
		<label className="flex items-center gap-1 text-sm font-medium text-kaefer-dark-grey">
			{label}
			{required && <span className="text-kaefer-red">*</span>}
		</label>
		{children}
		{hint && <p className="text-xs text-muted-foreground">{hint}</p>}
	</div>
)

export const ReadOnlyValue = ({
	label,
	value,
}: {
	label: string
	value: ReactNode
}) => (
	<div>
		<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
			{label}
		</p>
		<p className="mt-0.5 text-sm text-kaefer-dark-grey">{value || '—'}</p>
	</div>
)

export const Notice = ({
	tone = 'info',
	children,
}: {
	tone?: 'info' | 'warn' | 'lock'
	children: ReactNode
}) => (
	<div
		className={cn(
			'flex items-start gap-2 rounded-lg border px-3 py-2 text-sm',
			tone === 'info' &&
				'border-kaefer-astral-blue/40 bg-kaefer-astral-blue/10 text-kaefer-ocean-blue',
			tone === 'warn' &&
				'border-kaefer-honey-yellow/50 bg-kaefer-honey-yellow/10 text-kaefer-dark-grey',
			tone === 'lock' && 'border-kaefer-red/30 bg-kaefer-red/5 text-kaefer-red',
		)}
	>
		{tone === 'lock' && <Lock className="mt-0.5 size-4 shrink-0" />}
		<div>{children}</div>
	</div>
)

/** Placeholder de foto (o protótipo não carrega imagens reais). */
export const PhotoPlaceholder = ({
	tone,
	className,
	label,
}: {
	tone: string
	className?: string
	label?: string
}) => (
	<div className={cn('flex items-end bg-linear-to-br', tone, className)}>
		{label && (
			<span className="m-2 rounded bg-black/40 px-1.5 py-0.5 text-[11px] text-white">
				{label}
			</span>
		)}
	</div>
)

/** Diálogo que exige justificativa (cancelar, reabrir, reprovar, prorrogar...). */
export const JustificationDialog = ({
	open,
	onOpenChange,
	title,
	description,
	confirmLabel = 'Confirmar',
	danger,
	onConfirm,
}: {
	open: boolean
	onOpenChange: (v: boolean) => void
	title: string
	description?: string
	confirmLabel?: string
	danger?: boolean
	onConfirm: (justification: string) => void
}) => {
	const [text, setText] = useState('')
	const close = () => {
		setText('')
		onOpenChange(false)
	}
	return (
		<Dialog
			open={open}
			onOpenChange={(v) => (v ? onOpenChange(true) : close())}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>
						{description ??
							'A justificativa é obrigatória e fica registrada na auditoria.'}
					</DialogDescription>
				</DialogHeader>
				<Textarea
					autoFocus
					value={text}
					onChange={(e) => setText(e.target.value)}
					placeholder="Descreva a justificativa..."
					rows={4}
				/>
				<DialogFooter>
					<Button variant="cancel" onClick={close}>
						Voltar
					</Button>
					<Button
						variant={danger ? 'destructive' : 'primary'}
						disabled={text.trim().length < 5}
						onClick={() => {
							onConfirm(text.trim())
							close()
						}}
					>
						{confirmLabel}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

/** Botão "O que é?" ao lado de cada classificação, sem sair da tela. */
export const ClassificationHelp = ({ code }: { code: ClassificationCode }) => {
	const [open, setOpen] = useState(false)
	const info = CLASSIFICATIONS[code]
	return (
		<>
			<button
				type="button"
				aria-label={`O que é ${info.label}?`}
				className="inline-flex size-6 items-center justify-center rounded-full text-kaefer-pumpkin-orange hover:bg-kaefer-pumpkin-orange/10"
				onClick={(e) => {
					e.stopPropagation()
					setOpen(true)
				}}
			>
				<HelpCircle className="size-4" />
			</button>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>{info.label}</DialogTitle>
						<DialogDescription>
							Conforme IT G 21 005 · revisão vigente (mock)
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-3 text-sm">
						<ReadOnlyValue label="Definição oficial" value={info.definition} />
						<ReadOnlyValue
							label="Critérios de enquadramento"
							value={info.criteria}
						/>
						<ReadOnlyValue label="Exemplos práticos" value={info.examples} />
						<ReadOnlyValue
							label="Quando não se aplica"
							value={info.notApplicable}
						/>
						<div>
							<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
								Perguntas orientadoras
							</p>
							<ul className="mt-1 list-disc pl-5">
								{info.guidingQuestions.map((q) => (
									<li key={q}>{q}</li>
								))}
							</ul>
						</div>
						<p className="rounded bg-muted/40 p-2 text-xs text-muted-foreground">
							Referência: Portal SIG — procedimento vigente. O conteúdo desta
							ajuda será administrável e versionado.
						</p>
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}
